import React, { useState } from "react"
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Modal,
    MenuItem,
    Select,
    Avatar
} from "@mui/material"
import '../../../../../App.css'
import { useAuthInfo } from '@propelauth/react'

import {
    updateRoles,
    inviteUserToOrganization,
    listUsersInOrganization,
    addUserToTemplate,
    addUserToDocumentSet
} from '../../../../../utils/utils'

const ROLES = [
    { value: "Owner", label: "Owner" },
    { value: "Admin", label: "Admin" },
    { value: "Internal", label: "Internal" },
    { value: "Editor", label: "Editor" },
    { value: "Member", label: "Member" },
    { value: "Viewer", label: "Viewer" }
]

function UsersAndPermissionsModal({
    open,
    onClose,
    users = [],
    onInvite,
    onRoleChange,
    orgId,
    context = 'document_set', // 'document_set' or 'template'
    contextId = null, // document_set_id or template_id
    accessToken = null
}) {
    const authInfo = useAuthInfo();
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("Admin");
    const [selectedUserForInvite, setSelectedUserForInvite] = useState("");
    const [availableUsersForInvite, setAvailableUsersForInvite] = useState([]);

    // Ensure users are arrays
    const usersList = Array.isArray(users) ? users : [];

    // Log when users prop changes
    React.useEffect(() => {
        console.log('[UsersAndPermissionsModal] Users prop updated:', users);
    }, [users]);

    // Fetch available users when modal opens
    React.useEffect(() => {
        const fetchAvailableUsers = async () => {
            if (open && orgId) {
                try {
                    // Get the token from authInfo if accessToken prop is not provided
                    const token = accessToken || await authInfo.tokens.getAccessTokenForOrg(orgId);
                    const response = await listUsersInOrganization(orgId, token);
                    console.log('listUsersInOrganization response:', response);

                    // Handle different response formats
                    let users = [];
                    if (Array.isArray(response)) {
                        users = response;
                    } else if (response && Array.isArray(response.users)) {
                        users = response.users;
                    } else if (response && typeof response === 'object') {
                        // If response is an object with user data, try to extract it
                        users = Object.values(response).filter(item => item && item.user_id);
                    }

                    setAvailableUsersForInvite(users);
                } catch (error) {
                    console.error('Error fetching available users:', error);
                    setAvailableUsersForInvite([]);
                }
            }
        };
        fetchAvailableUsers();
    }, [open, orgId, accessToken]); // Removed authInfo from dependencies to prevent infinite loop

    const getUsersRole = (user, orgId) => {
        console.log('[getUsersRole] Getting role for user:', user.user_id)
        console.log('[getUsersRole] From org:', orgId)
        console.log('[getUsersRole] User object:', user)

        // Check if role is directly on user object (after update)
        if (user.role) {
            console.log('[getUsersRole] Found direct role:', user.role)
            return user.role
        }

        // Otherwise, check nested structure
        if (user.org_id_to_org_info && user.org_id_to_org_info[orgId]) {
            const user_org = user.org_id_to_org_info[orgId]
            console.log('[getUsersRole] user_org:', user_org)

            const role = user_org.user_role || (user_org.inherited_user_roles_plus_current_role && user_org.inherited_user_roles_plus_current_role[0])
            if (role) {
                console.log('[getUsersRole] Found nested role:', role)
                return role
            }
        }
        console.log('[getUsersRole] role not found, returning Viewer')
        return "Viewer"
    }

    // Handle invite button click
    const handleInvite = async () => {
      if (selectedUserForInvite) {
        const selectedUser = availableUsersForInvite.find(u => u.user_id === selectedUserForInvite);
        if (selectedUser) {
            try {
                // Get the token from authInfo if accessToken prop is not provided
                const token = accessToken || await authInfo.tokens.getAccessTokenForOrg(orgId);

                let jsonPayload;
                let apiFunction;

                if (context === 'template') {
                    jsonPayload = {
                        email: selectedUser.email,
                        template_id: contextId
                    };
                    apiFunction = addUserToTemplate;
                } else {
                    jsonPayload = {
                        email: selectedUser.email,
                        document_set_id: contextId
                    };
                    apiFunction = addUserToDocumentSet;
                }

                const response = await apiFunction(jsonPayload, token);
                console.log('Add user API response:', response);

                // Call the parent's onInvite callback if provided and WAIT for it to complete
                if (onInvite) {
                    await onInvite(selectedUser.email, inviteRole);
                }

                // Clear the form after successful invite and after parent has refreshed data
                setSelectedUserForInvite("");
                setInviteEmail("");
                setInviteRole("Admin");
            } catch (error) {
                console.error('Error adding user:', error);
            }
        }
      }
    };

    // Handle user selection change
    const handleUserSelectionChange = (event) => {
        const userId = event.target.value;
        setSelectedUserForInvite(userId);

        // Find the selected user and set their email
        const selectedUser = availableUsersForInvite.find(u => u.user_id === userId);
        if (selectedUser) {
            setInviteEmail(selectedUser.email);
        }
    };

    // Handle role change for a user
    const handleRoleChange = (userId, newRole) => {
        console.log('[Modal handleRoleChange] User ID:', userId)
        console.log('[Modal handleRoleChange] New Role:', newRole)
        if (onRoleChange) {
            onRoleChange(userId, newRole)
        }
    };

    // Handle close
    const handleClose = () => {
      setInviteEmail("");
      setInviteRole("Admin");
      setSelectedUserForInvite("");
      onClose();
    };
  
    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100vw",
                height: "100vh",
                zIndex: 1300
            }}
        >
            <Box
                sx={{
                    background: "#f9fafb",
                    borderRadius: 2,
                    boxShadow: "0 10px 40px rgba(0,0,0,0.16)",
                    width: "548px",
                    height: "auto",
                    maxHeight: "80vh",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative"
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <Box sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                    display: "flex",
                    flexDirection: "column"
                }}>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{
                            color: "#111827",
                            padding: 0,
                            width: "fit-content",
                            mb: 2,
                            "&:hover": {
                                backgroundColor: "transparent"
                            }
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </IconButton>
                    <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#1F2937", lineHeight: 1, mb: 2.5 }}>
                        Users
                    </Typography>

                    {/* Invite section */}
                    <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                        {/* User dropdown */}
                        <Box sx={{
                            display: "flex",
                            flex: 1,
                            border: "1px solid #E5E7EB",
                            borderRadius: '4px',
                            overflow: "hidden",
                            backgroundColor: "#fff"
                        }}>
                            <Select
                                fullWidth
                                displayEmpty
                                value={selectedUserForInvite}
                                onChange={handleUserSelectionChange}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <Typography sx={{ color: "#9CA3AF", fontSize: 13 }}>Select a user</Typography>;
                                    }
                                    const selectedUser = availableUsersForInvite.find(u => u.user_id === selected);
                                    return selectedUser?.email || "";
                                }}
                                sx={{
                                    flex: 1,
                                    borderRadius: 0,
                                    fontSize: 13,
                                    border: "none",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                    "& .MuiSelect-select": {
                                        py: 1,
                                        px: 1.75,
                                        color: "#111827",
                                        fontWeight: 400,
                                        fontSize: 13,
                                        lineHeight: '16px'
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "#6B7280",
                                        fontSize: 18
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            "& .MuiMenuItem-root": {
                                                fontSize: 13,
                                                lineHeight: '16px'
                                            }
                                        }
                                    }
                                }}
                            >
                                {Array.isArray(availableUsersForInvite) && availableUsersForInvite.map((user) => (
                                    <MenuItem key={user.user_id} value={user.user_id}>
                                        {user.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        {/* Invite button - separate component */}
                        <Button
                            variant="contained"
                            onClick={handleInvite}
                            disabled={!selectedUserForInvite}
                            sx={{
                                borderRadius: '4px',
                                backgroundColor: "#1F477D",
                                color: "#FFFFFF",
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '20px',
                                px: 2.5,
                                py: 1,
                                textTransform: "none",
                                boxShadow: "none",
                                minWidth: "auto",
                                border: "none",
                                "&:hover": {
                                    backgroundColor: "#183864",
                                    boxShadow: "none"
                                },
                                "&:disabled": {
                                    backgroundColor: "#E5E7EB",
                                    color: "#6B7280",
                                    cursor: "not-allowed"
                                },
                            }}
                        >
                            Invite
                        </Button>
                    </Box>
                </Box>

                {/* User List Container */}
                <Box sx={{ px: 3, pb: 3, flex: 1, minHeight: 0 }}>
                    {/* User count */}
                    <Typography 
                        sx={{ 
                            fontSize: '14px', 
                            lineHeight: '20px',
                            color: "#374151", 
                            mb: 1.5,
                            fontWeight: 500
                        }}
                    >
                        Users · {usersList.length}
                    </Typography>
                    <Box sx={{
                        backgroundColor: "#fff",
                        borderRadius: '16px',
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        maxHeight: '100%',
                        overflow: "auto"
                    }}>
                        {usersList.length === 0 ? (
                            <Typography sx={{
                                textAlign: "center",
                                color: "#9CA3AF",
                                py: 4,
                                fontSize: 13
                            }}>
                                No users found
                            </Typography>
                        ) : (
                            usersList.map((user) => (
                                <Box
                                    key={user.user_id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                                        <Avatar
                                            src={user.picture_url}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                bgcolor: "#D1D5DB",
                                                color: "#6B7280",
                                                fontSize: 12,
                                                fontWeight: 500
                                            }}
                                        >
                                            {user.name ? user.name[0].toUpperCase() : (user.email ? user.email[0].toUpperCase() : "U")}
                                        </Avatar>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                lineHeight: '16px',
                                                color: "#374151",
                                                fontWeight: 400
                                            }}
                                        >
                                            {user.name || user.email}
                                        </Typography>
                                    </Box>
                                    <Select
                                        value={getUsersRole(user, orgId)}
                                        onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
                                        sx={{
                                            minWidth: '120px',
                                            height: '28px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            lineHeight: '16px',
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#E5E7EB",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#D1D5DB",
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "#D1D5DB",
                                            },
                                            "& .MuiSelect-select": {
                                                py: 0.5,
                                                px: 1.5,
                                                color: "#111827",
                                                fontWeight: 400,
                                                fontSize: '12px',
                                                lineHeight: '16px'
                                            },
                                            "& .MuiSvgIcon-root": {
                                                color: "#6B7280",
                                                fontSize: 18
                                            }
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    "& .MuiMenuItem-root": {
                                                        fontSize: '12px',
                                                        lineHeight: '16px'
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        {ROLES.map((role) => (
                                            <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}

export default UsersAndPermissionsModal